(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root || !root.document) return;
  if (root.AlawooderJournalListing) {
    root.AlawooderJournalListing.init(root.document);
    return;
  }
  root.AlawooderJournalListing = api;
  api.install(root);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';

  const FEED_SIZE = 50;
  const PAGE_SIZE = 6;
  const MAX_FEED_PAGES = 500;
  const instances = new Map();
  const feedCache = new Map();

  function assert(condition, message) {
    if (!condition) throw new Error(message);
  }

  function readPage(value) {
    const text = String(value == null ? '' : value);
    return /^[1-9]\d*$/.test(text) && Number.isSafeInteger(Number(text)) ? Number(text) : 1;
  }

  function safeBlogUrl(value, locationHref) {
    assert(typeof value === 'string' && value.trim(), 'Missing journal blog URL');
    const location = new URL(locationHref);
    const url = new URL(value, location);
    assert(['http:', 'https:'].includes(url.protocol) && url.origin === location.origin, 'Invalid journal origin');
    assert(/\/blogs\/[^/]+\/?$/.test(url.pathname), 'Invalid journal blog path');
    assert(url.username === '' && url.password === '', 'Invalid journal URL credentials');
    url.search = '';
    url.hash = '';
    return url;
  }

  function pageUrl(locationHref, page) {
    const url = new URL(locationHref);
    assert(['http:', 'https:'].includes(url.protocol), 'Invalid page URL');
    assert(Number.isSafeInteger(page) && page > 0, 'Invalid page number');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    if (page === 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    url.hash = '';
    return url.href;
  }

  function validateFeed(raw, expectedPage) {
    assert(raw && typeof raw === 'object' && !Array.isArray(raw), 'Invalid journal feed');
    assert(Number.isSafeInteger(raw.total) && raw.total >= 0, 'Invalid journal total');
    const expectedPages = Math.max(1, Math.ceil(raw.total / FEED_SIZE));
    const pages = raw.total === 0 && raw.pages === 0 ? 1 : raw.pages;
    assert(Number.isSafeInteger(pages) && pages === expectedPages && pages <= MAX_FEED_PAGES, 'Invalid journal page count');
    assert(raw.page === expectedPage && raw.page >= 1 && raw.page <= pages, 'Unexpected journal page');
    assert(typeof raw.blogId === 'string' && raw.blogId.trim(), 'Missing journal blog ID');
    assert(Array.isArray(raw.articles), 'Missing journal articles');
    const count = Math.max(0, Math.min(FEED_SIZE, raw.total - (raw.page - 1) * FEED_SIZE));
    assert(raw.articles.length === count, 'Incomplete journal feed page');
    raw.articles.forEach(article => {
      assert(article && typeof article.id === 'string' && article.id.trim() === article.id && article.id, 'Invalid article ID');
      assert(typeof article.html === 'string' && article.html.trim(), 'Missing article markup');
    });
    return { page: raw.page, pages, total: raw.total, blogId: raw.blogId, articles: raw.articles };
  }

  function mergeFeeds(feeds) {
    assert(Array.isArray(feeds) && feeds.length > 0, 'Missing journal feed pages');
    const sorted = feeds.slice().sort((a, b) => a.page - b.page);
    const first = validateFeed(sorted[0], 1);
    assert(sorted.length === first.pages, 'Incomplete journal feed');
    const ids = new Set();
    const articles = [];
    sorted.forEach((raw, index) => {
      const feed = validateFeed(raw, index + 1);
      assert(feed.blogId === first.blogId && feed.total === first.total && feed.pages === first.pages, 'Journal feed changed during loading');
      feed.articles.forEach(article => {
        assert(!ids.has(article.id), 'Duplicate journal article');
        ids.add(article.id);
        articles.push(article);
      });
    });
    assert(articles.length === first.total && ids.size === first.total, 'Journal total does not match');
    return { articles, total: first.total, blogId: first.blogId };
  }

  function pageModel(articles, options) {
    const featuredId = options.featuredAuto ? (articles[0] && articles[0].id) : options.featuredId;
    const filtered = articles.filter(article => article.id !== featuredId);
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const requested = readPage(options.page);
    const page = Math.min(requested, pages);
    return { articles: filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), total: filtered.length, pages, page, requested, featuredId: featuredId || '' };
  }

  function paginationParts(page, pages) {
    const visible = new Set([1, pages, page - 1, page, page + 1]);
    if (page <= 3) [2, 3, 4].forEach(value => visible.add(value));
    if (page >= pages - 2) [pages - 3, pages - 2, pages - 1].forEach(value => visible.add(value));
    const numbers = Array.from(visible).filter(value => value >= 1 && value <= pages).sort((a, b) => a - b);
    const parts = [];
    numbers.forEach((value, index) => {
      const previous = numbers[index - 1];
      if (index && value - previous === 2) parts.push(previous + 1);
      else if (index && value - previous > 2) parts.push('…');
      parts.push(value);
    });
    return parts;
  }

  function configFor(listing, locationHref) {
    const data = listing.dataset;
    assert(data.pageSize === String(PAGE_SIZE), 'Unexpected journal page size');
    const blogUrl = safeBlogUrl(data.blogUrl, locationHref);
    const current = new URL(locationHref);
    assert(current.pathname.replace(/\/$/, '') === blogUrl.pathname.replace(/\/$/, ''), 'Journal route does not match');
    const label = (value, fallback) => typeof value === 'string' && value.trim() ? value.trim() : fallback;
    return {
      blogUrl, featuredId: typeof data.featuredId === 'string' ? data.featuredId.trim() : '',
      featuredAuto: data.featuredAuto === 'true', showDate: data.showDate === 'true',
      readLabel: label(data.readLabel, 'Read the story'), paginationLabel: label(data.paginationLabel, 'Journal pages'),
      previousLabel: label(data.previousLabel, 'Previous'), nextLabel: label(data.nextLabel, 'Next')
    };
  }

  function parseFeedMarkup(markup, document) {
    const template = document.createElement('template');
    template.innerHTML = markup;
    const scripts = template.content.querySelectorAll('script[type="application/json"][data-journal-feed]');
    assert(scripts.length === 1, 'Missing journal JSON');
    return JSON.parse(scripts[0].textContent);
  }

  async function loadArticles(config, environment) {
    const key = config.blogUrl.href;
    if (feedCache.has(key)) return feedCache.get(key);
    const loading = (async () => {
      async function fetchPage(page) {
        const url = new URL(config.blogUrl);
        url.searchParams.set('section_id', 'aw-journal-feed');
        url.searchParams.set('page', String(page));
        const controller = new environment.AbortController();
        const timer = environment.setTimeout(() => controller.abort(), 12000);
        try {
          const response = await environment.fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'text/html' } });
          assert(response.ok, 'Journal feed request failed');
          if (response.url) assert(new URL(response.url).origin === config.blogUrl.origin, 'Unexpected feed redirect');
          return validateFeed(parseFeedMarkup(await response.text(), environment.document), page);
        } finally {
          environment.clearTimeout(timer);
        }
      }
      const first = await fetchPage(1);
      const feeds = [first];
      let next = 2;
      async function worker() {
        while (next <= first.pages) {
          const page = next++;
          feeds[page - 1] = await fetchPage(page);
        }
      }
      await Promise.all(Array.from({ length: Math.min(4, first.pages - 1) }, worker));
      return mergeFeeds(feeds);
    })();
    feedCache.set(key, loading);
    try { return await loading; }
    catch (error) { if (feedCache.get(key) === loading) feedCache.delete(key); throw error; }
  }

  function prepareCard(article, config, document) {
    // Only the current page's six snippets are parsed. Templates keep images inert until commit.
    const template = document.createElement('template');
    template.innerHTML = article.html;
    assert(template.content.childElementCount === 1, 'Unexpected journal card markup');
    const card = template.content.firstElementChild;
    assert(card.matches('.ah-journal-card[data-journal-article-id]') && card.dataset.journalArticleId === article.id, 'Journal card ID does not match');
    assert(!card.querySelector('script, iframe, object, embed, base'), 'Invalid journal card content');
    card.querySelectorAll('a[href]').forEach(link => {
      const url = new URL(link.getAttribute('href'), config.blogUrl);
      assert(['http:', 'https:'].includes(url.protocol) && url.origin === config.blogUrl.origin, 'Invalid article link');
    });
    const title = card.querySelector('h2 a, h3 a');
    assert(title && title.textContent.trim(), 'Missing article title');
    const reads = card.querySelectorAll('.ah-journal-read');
    assert(reads.length > 0, 'Missing article read link');
    reads.forEach(link => {
      const label = link.querySelector('.ah-journal-read-label');
      assert(label, 'Missing article read label');
      label.textContent = config.readLabel;
      link.setAttribute('aria-label', `${config.readLabel}: ${title.textContent.trim()}`);
    });
    if (!config.showDate) card.querySelectorAll('time').forEach(time => time.remove());
    return card;
  }

  function preparePagination(model, config, document, locationHref) {
    if (model.pages <= 1) return null;
    const nav = document.createElement('nav');
    nav.className = 'ah-journal-pagination';
    nav.setAttribute('aria-label', config.paginationLabel);
    function arrow(page, text, symbol, previous) {
      const link = document.createElement('a');
      link.className = 'ah-journal-page-arrow';
      link.href = pageUrl(locationHref, page);
      link.rel = previous ? 'prev' : 'next';
      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = symbol;
      if (previous) link.append(icon, document.createTextNode(text));
      else link.append(document.createTextNode(text), icon);
      return link;
    }
    if (model.page > 1) nav.append(arrow(model.page - 1, config.previousLabel, '←', true));
    const numbers = document.createElement('div');
    numbers.className = 'ah-journal-page-numbers';
    paginationParts(model.page, model.pages).forEach(part => {
      const current = part === model.page;
      const element = document.createElement(typeof part === 'number' && !current ? 'a' : 'span');
      element.textContent = String(part);
      if (current) element.setAttribute('aria-current', 'page');
      else if (typeof part === 'number') element.href = pageUrl(locationHref, part);
      numbers.append(element);
    });
    nav.append(numbers);
    if (model.page < model.pages) nav.append(arrow(model.page + 1, config.nextLabel, '→', false));
    return nav;
  }

  async function hydrate(listing, state, environment) {
    const grid = listing.querySelector('.ah-journal-grid');
    if (!grid) return;
    const locationHref = environment.location.href;
    const config = configFor(listing, locationHref);
    const feed = await loadArticles(config, environment);
    if (state.cancelled || !listing.isConnected || environment.location.href !== locationHref) return;
    const rawPage = new URL(locationHref).searchParams.get('page');
    const model = pageModel(feed.articles, { featuredId: config.featuredId, featuredAuto: config.featuredAuto, page: rawPage });
    const cards = environment.document.createDocumentFragment();
    model.articles.forEach(article => cards.append(prepareCard(article, config, environment.document)));
    const nav = preparePagination(model, config, environment.document, locationHref);
    const previousNav = listing.querySelector('.ah-journal-pagination');
    // All feed pages and selected cards have passed validation before touching the SSR listing.
    if (model.page !== model.requested || (rawPage !== null && rawPage !== String(model.page))) {
      environment.history.replaceState(environment.history.state, '', pageUrl(locationHref, model.page));
    }
    grid.replaceChildren(cards);
    if (previousNav) { if (nav) previousNav.replaceWith(nav); else previousNav.remove(); }
    else if (nav) grid.after(nav);
    listing.dataset.journalListingState = 'ready';
    listing.dataset.journalListingTotal = String(model.total);
    listing.dataset.journalListingPage = String(model.page);
    listing.dataset.journalListingPages = String(model.pages);
  }

  function init(root, environment) {
    environment = environment || (typeof window === 'undefined' ? null : window);
    if (!environment) return;
    const listings = Array.from(root.querySelectorAll('[data-journal-listing]'));
    if (root.matches && root.matches('[data-journal-listing]')) listings.unshift(root);
    listings.forEach(listing => {
      if (instances.has(listing)) return;
      const state = { cancelled: false };
      instances.set(listing, state);
      listing.dataset.journalListingState = 'loading';
      // Failure leaves the complete native SSR fallback and its links unchanged.
      state.ready = hydrate(listing, state, environment)
        .catch(() => { if (!state.cancelled && listing.isConnected) return hydrate(listing, state, environment); })
        .catch(() => { if (!state.cancelled && listing.isConnected) listing.dataset.journalListingState = 'fallback'; });
    });
  }

  function install(environment) {
    const document = environment.document;
    document.addEventListener('shopify:section:load', event => init(event.target, environment));
    document.addEventListener('shopify:section:unload', event => {
      instances.forEach((state, listing) => {
        if (event.target === listing || event.target.contains(listing)) {
          state.cancelled = true;
          instances.delete(listing);
        }
      });
    });
    init(document, environment);
  }

  return { init, install, readPage, safeBlogUrl, pageUrl, validateFeed, mergeFeeds, pageModel, paginationParts };
});
